import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LifebuoyIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useVersionedApi } from '../../utils/versionedApi';
import Modal from '../ui/Modal';
import { createCobrowseSession } from './cobrowseSession';

const button = 'min-h-11 rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

export default function LiveSupport({ sessionFactory = createCobrowseSession, apiOverride }) {
  const defaultApi = useVersionedApi('v1');
  const api = apiOverride || defaultApi;
  const [open, setOpen] = useState(false);
  const [availability, setAvailability] = useState('idle');
  const [state, setState] = useState({ status: 'idle' });
  const [consent, setConsent] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const controller = useRef(null);
  const mounted = useRef(true);
  const busy = ['starting', 'waiting', 'active', 'stopping', 'stop-failed'].includes(state.status);

  useEffect(() => {
    mounted.current = true;
    const stop = () => { void controller.current?.stop(); };
    const storageChanged = event => {
      if (['evv_token', 'evv_user', 'evv_user_cache', 'organizationId'].includes(event.key) || event.key === null) stop();
    };
    window.addEventListener('pagehide', stop);
    window.addEventListener('offline', stop);
    window.addEventListener('storage', storageChanged);
    return () => {
      mounted.current = false;
      stop();
      window.removeEventListener('pagehide', stop);
      window.removeEventListener('offline', stop);
      window.removeEventListener('storage', storageChanged);
    };
  }, []);

  async function show() {
    setOpen(true);
    if (busy) return;
    setAvailability('loading');
    try {
      const response = await api.get('/support/cobrowse/config');
      if (mounted.current) setAvailability(response.data.data.available ? 'available' : 'unavailable');
    } catch {
      if (mounted.current) setAvailability('error');
    }
  }

  function start() {
    if (busy || !agreed || availability !== 'available') return;
    setState({ status: 'starting' });
    controller.current = sessionFactory({ api,
      onChange: next => { if (mounted.current) setState(next); },
      onConsent: next => { if (mounted.current) setConsent(next); },
    });
    void controller.current.start();
  }

  function stop() {
    setState(previous => ({ ...previous, status: 'stopping' }));
    void controller.current?.stop();
    setAgreed(false);
  }

  return <div data-cobrowse-private>
    <button type="button" className={button} onClick={show} aria-label="Live support">
      <LifebuoyIcon className="inline h-5 w-5 sm:mr-2" aria-hidden="true" /><span className="hidden sm:inline">Live support</span>
    </button>
    {busy && createPortal(<div role="status" className="fixed bottom-4 left-4 right-4 sm:left-auto sm:w-96 z-40 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 shadow-lg text-gray-900 dark:text-white" data-cobrowse-private>
      <p className="font-semibold">{state.status === 'active' ? (state.controlled ? 'Support can navigate' : 'Support is viewing this page') : state.status === 'stop-failed' ? 'Finish disconnecting' : state.status === 'stopping' ? 'Disconnecting support…' : 'Live support is waiting'}</p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{state.message || 'You can end the session at any time.'}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={button} onClick={() => setOpen(true)}>Session details</button>
        {state.controlled && <button type="button" className={button} onClick={() => controller.current?.revokeControl()}>Take back control</button>}
        <button type="button" className={button} onClick={stop} disabled={state.status === 'stopping' || state.status === 'stop-failed'}>Stop sharing</button>
      </div>
    </div>, document.body)}
    <Modal portal isOpen={open && !consent} onClose={() => setOpen(false)} title="Live support" size="md">
      <div className="p-6 space-y-5 text-sm text-gray-700 dark:text-gray-200" data-cobrowse-private>
        {!busy && <>
          <p>Work through a problem together with AraSync support. Stay on this tab while the support person connects.</p>
          {availability === 'loading' && <p role="status">Checking availability…</p>}
          {availability === 'unavailable' && <p role="status">Live support is not enabled for your organization yet. You can still use Support Tickets.</p>}
          {availability === 'error' && <div role="alert">We couldn’t check live support. <button type="button" className="underline" onClick={show}>Try again</button></div>}
          {availability === 'available' && <>
            <p>After you approve viewing, support can see this page, including any unmasked client information. Form fields, signatures, images, and embedded documents are hidden. This session is not recorded. Close anything you don’t want to share.</p>
            <p>You’ll approve navigation control separately. Support can use the main menu; you remain responsible for entering information and completing actions.</p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={event => setAgreed(event.target.checked)} className="mt-1 h-4 w-4" />
              <span>I want to connect with AraSync support and understand what may be shared.</span>
            </label>
            <button type="button" onClick={start} disabled={!agreed} className={button}>Get a support code</button>
          </>}
        </>}
        {state.status === 'starting' && <p role="status">Preparing your support session…</p>}
        {state.status === 'waiting' && <>
          <p>Read this code to the AraSync support person you’re speaking with. You’ll be asked before they can see your page.</p>
          <p aria-label="Support code" className="text-3xl font-semibold tracking-[0.2em] tabular-nums text-gray-900 dark:text-white">{state.code}</p>
          <p>Expires at {new Date(state.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. Keep this tab open.</p>
        </>}
        {state.status === 'active' && <p role="status">{state.controlled ? 'Navigation control is on. Clinical, billing, and other action buttons cannot be controlled.' : 'Support is viewing your page. Navigation control is off.'}</p>}
        {state.status === 'ended' && <p role={state.message ? 'alert' : 'status'}>{state.message || 'Your support session has ended.'}</p>}
        {state.auditFailed && <p role="alert">The session history could not be updated. Please mention this in your support ticket.</p>}
        {state.status === 'stop-failed' && <p role="alert">{state.message}</p>}
        {busy && <button type="button" className={button} onClick={stop} disabled={state.status === 'stopping' || state.status === 'stop-failed'}><XMarkIcon className="inline h-4 w-4 mr-2" />Stop sharing</button>}
      </div>
    </Modal>
    <Modal portal isOpen={Boolean(consent)} onClose={() => consent?.respond(false)} title={consent?.kind === 'control' ? 'Allow navigation control?' : 'Allow support to view this page?'} size="md">
      <div className="p-6 space-y-4 text-sm text-gray-700 dark:text-gray-200" data-cobrowse-private>
        <p><strong>{consent?.agentName}</strong> {consent?.kind === 'control' ? 'would like to use the main navigation menu. Other controls remain yours.' : 'would like to see this AraSync page. Unmasked client information on the page will be visible.'}</p>
        <p>You can stop sharing at any time.</p>
        <div className="flex flex-wrap gap-3">
          <button type="button" className={button} onClick={() => consent?.respond(false)}>Decline</button>
          <button type="button" className={button} onClick={() => consent?.respond(true)}>{consent?.kind === 'control' ? 'Allow navigation' : 'Allow viewing'}</button>
        </div>
      </div>
    </Modal>
  </div>;
}
